import RadialGraph from "../../HomePage/SummaryCard/Gauge";
import { getColorCode } from "../../../utils/monitoring-helpers";
import classes from "../homePageNew.module.css";
import MonitoringLlmLogo from "./MonitoringLlmLogo";

function avg(arr, pick) {
  const vals = arr.map(pick).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

function fmt(value, decimals = 0) {
  return value == null ? "—" : Number(value).toFixed(decimals);
}

/**
 * Left card — Visibility/Sentiment tiles, Brand Index gauge, and the
 * LLM PERFORMANCE table. All derived from the selected combination's
 * provider-grouped latest/previous scores (monitoringDerive.js), already
 * filtered to whichever providers are checked in the multiselect above.
 *
 * Gauge is the exact same RadialGraph the old Home page's cards use
 * (src/luminaire/HomePage/SummaryCard/Gauge.js) — reused directly rather
 * than reimplemented. Table coloring reuses that same page's
 * getColorCode() (monitoring-helpers.js): RBI thresholds for Index, the
 * visibility thresholds for Visibility, and the relative-to-scale
 * thresholds (against the fixed 50-point scale) for Sentiment. Only the
 * Index column gets an up/down arrow vs the previous run — that's the
 * one score also shown as the "N Current" delta on the gauge above, so
 * it's the one worth repeating per-row; Visibility/Sentiment are shown
 * as plain values.
 */
export default function MonitoringSummaryCard({ selectedProviderLlms }) {
  const visibility = avg(selectedProviderLlms, (l) => l.latest?.visibilityScore);
  const sentiment = avg(selectedProviderLlms, (l) => l.latest?.sentimentScore);
  const brandIndex = avg(selectedProviderLlms, (l) => l.latest?.indexScore);

  const withPrevious = selectedProviderLlms.filter((l) => l.previous);
  // RadialGraph computes its own delta internally from value/pastValue —
  // just pass the raw averages, no need to compute the difference here too.
  const prevBrandIndex = avg(withPrevious, (l) => l.previous.indexScore);

  return (
    <div className={classes.monSummaryCard}>
      <div className={classes.monStatPair}>
        <div className={classes.monStatBox}>
          <div className={classes.monStatLabel}>Visibility</div>
          <div className={classes.monStatVal}>{fmt(visibility, 0)}</div>
        </div>
        <div className={classes.monStatBox}>
          <div className={classes.monStatLabel}>Sentiment</div>
          <div className={classes.monStatVal}>{fmt(sentiment, 0)}</div>
        </div>
      </div>

      <div className={classes.monArcWrap}>
        <div className={classes.monArcTitle}>Brand Index Score</div>
        <RadialGraph
          value={brandIndex != null ? { score: brandIndex } : null}
          pastValue={prevBrandIndex != null ? { score: prevBrandIndex } : null}
          Range={100}
          frequencyLabel="Current"
        />
      </div>

      <div className={classes.monLlmListTitle}>LLM PERFORMANCE</div>
      <div className={classes.monTableWrap}>
        <table className={classes.monTable}>
          <thead>
            <tr>
              <th>LLM</th>
              <th>Index</th>
              <th>Visibility</th>
              <th>Sentiment</th>
            </tr>
          </thead>
          <tbody>
            {selectedProviderLlms.map((l) => {
              const idx = l.latest?.indexScore;
              const vis = l.latest?.visibilityScore;
              const sent = l.latest?.sentimentScore;
              const dIdx = l.previous ? idx - l.previous.indexScore : null;
              return (
                <tr key={l.provider}>
                  <td className={classes.monTableLlmCell} title={l.label}>
                    <MonitoringLlmLogo domain={l.domain} provider={l.provider} name={l.name} />
                    {l.name}
                  </td>
                  <td style={{ color: idx != null ? getColorCode(idx, undefined, "RBI") : undefined }}>
                    {fmt(idx)}
                    {dIdx != null && dIdx !== 0 && <span className={classes.monDelta}>{dIdx > 0 ? "▲" : "▼"}</span>}
                  </td>
                  <td style={{ color: vis != null ? getColorCode(vis, undefined, "visibility") : undefined }}>{fmt(vis)}</td>
                  <td style={{ color: sent != null ? getColorCode(sent, 50) : undefined }}>{fmt(sent)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
